import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Header from '../../components/Header/Header';
import Button from '../../components/Button/Button';
import alert from '../../helpers/alert';
import extractErrorMessage from '../../utils/extractErrorMessage';
import icon from '../../assets/sidebar/cars.svg';
import styles from './priceList.module.css';
import {
  applyPriceList,
  getPriceList,
  getPriceMarks,
  rollbackPriceList,
  savePriceMeta,
  savePriceRules,
} from '../../api/prices';
import {
  ATTRS,
  NOT_SET,
  addBranch,
  buildTree,
  collapseNode,
  coverage,
  makeNode,
  normalizeEngineVolume,
  pricedInside,
  splitNode,
  subtreePaths,
  subtreeStats,
  treeToRules,
  unrepresentableRules,
  updateNode,
} from './priceTree';

const money = (v) => (v == null ? '' : Number(v).toLocaleString('ru-RU'));
const parseMoney = (v) => {
  const digits = String(v).replace(/\D/g, '');
  return digits ? Number(digits) : null;
};
const dateInput = (v) => (v ? String(v).slice(0, 10) : '');

const PriceList = () => {
  const [marks, setMarks] = useState([]);
  const [markId, setMarkId] = useState(null);
  const [data, setData] = useState(null);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  // Откат доступен только после настоящего применения: у предпросмотра batchId
  // тоже есть, но за ним ничего не записано
  const [appliedBatch, setAppliedBatch] = useState(null);
  // Сворачивание ветки с проставленными ценами подтверждается вторым кликом —
  // тот же паттерн, что у очистки стока: без модалки, с самосбросом
  const [collapseConfirm, setCollapseConfirm] = useState(null);
  // Какие ветки раскрыты. По умолчанию свёрнуто всё: у Kia при полном разбиении
  // дерево разрастается до ~180 строк, и искать в нём одну модель невозможно
  const [openPaths, setOpenPaths] = useState(() => new Set());
  const [query, setQuery] = useState('');
  // Применение меняет цены сотням машин и стоит рядом с безобидными кнопками —
  // одного клика для этого мало. Первый клик считает предпросмотр и показывает
  // число прямо на кнопке, применяет только второй
  const [applyConfirm, setApplyConfirm] = useState(null);
  // Черновики ручного ввода веток (год, объём) — по ключу ветки, чтобы соседние
  // строки не делили одно поле
  const [addDraft, setAddDraft] = useState({});

  useEffect(() => {
    getPriceMarks()
      .then((res) => {
        setMarks(res.data || []);
        // Загрузку здесь не снимаем: следом грузится прайс выбранной марки, и между
        // двумя запросами экран на кадр показывал «нет машин в стоке» без данных
        if (res.data?.length) setMarkId(res.data[0].markId);
        else setLoading(false);
      })
      .catch((err) => {
        alert.error(extractErrorMessage(err) || 'Не удалось загрузить список марок');
        setLoading(false);
      });
  }, []);

  const load = useCallback((id) => {
    setLoading(true);
    setPreview(null);
    setAppliedBatch(null);
    setOpenPaths(new Set());
    setQuery('');
    setApplyConfirm(null);
    getPriceList(id)
      .then((res) => {
        setData(res.data);
        setTree(buildTree(res.data.stock || [], res.data.rules || []));
      })
      .catch((err) => {
        // Иначе под новой маркой осталось бы дерево предыдущей — и сохранилось бы в неё
        setData(null);
        setTree([]);
        alert.error(extractErrorMessage(err) || 'Не удалось загрузить прайс');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (markId) load(markId);
  }, [markId, load]);

  useEffect(() => {
    if (!collapseConfirm) return undefined;
    const timer = setTimeout(() => setCollapseConfirm(null), 4000);
    return () => clearTimeout(timer);
  }, [collapseConfirm]);

  useEffect(() => {
    if (!applyConfirm) return undefined;
    const timer = setTimeout(() => setApplyConfirm(null), 6000);
    return () => clearTimeout(timer);
  }, [applyConfirm]);

  const names = useMemo(() => {
    const models = {};
    (data?.models || []).forEach((m) => {
      models[m.id] = m.name;
    });
    const compl = {};
    (data?.complectations || []).forEach((c) => {
      compl[c.id] = c.title;
    });
    const wd = {};
    (data?.wd || []).forEach((w) => {
      wd[w.id] = w.title;
    });
    return { models, compl, wd };
  }, [data]);

  const label = (fixed, depth) => {
    if (depth === 0) return names.models[fixed.modelId] || `Модель #${fixed.modelId}`;
    const attr = Object.keys(fixed)[Object.keys(fixed).length - 1];
    const value = fixed[attr];
    if (attr === 'complectationId') return value ? names.compl[value] || `#${value}` : NOT_SET;
    if (attr === 'wdId') return value ? names.wd[value] || `#${value}` : NOT_SET;
    return String(value ?? '').length ? String(value) : NOT_SET;
  };

  const stats = useMemo(() => coverage(tree), [tree]);

  const pathKey = (node) => JSON.stringify(node.fixed);
  const isOpen = (node) => openPaths.has(pathKey(node));
  // Раскрываем и сворачиваем ветку целиком. По уровню за клик было мучением:
  // у Carnival цена стоит на объёме, то есть на третьем уровне, и первые два
  // клика показывали только строки с прочерком
  const toggleOpen = (node) =>
    setOpenPaths((prev) => {
      const next = new Set(prev);
      const paths = subtreePaths(node);
      if (next.has(paths[0])) paths.forEach((p) => next.delete(p));
      else paths.forEach((p) => next.add(p));
      return next;
    });

  // Поиск по названию модели. Когда совпала ровно одна — сразу раскрываем её:
  // это и есть типовой сценарий «ищу одну марку, чтобы поправить цену»
  const visibleTree = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tree;
    return tree.filter((n) => label(n.fixed, 0).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, query, names]);

  useEffect(() => {
    if (visibleTree.length === 1 && query.trim()) {
      // Нашли одну модель — показываем её целиком, а не первый уровень
      setOpenPaths((prev) => {
        const next = new Set(prev);
        subtreePaths(visibleTree[0]).forEach((p) => next.add(p));
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTree.length, query]);

  const patch = (node, fn) => {
    // Дерево изменилось — посчитанное подтверждение больше не про этот прайс
    setApplyConfirm(null);
    setTree((prev) => updateNode(prev, node, fn));
  };

  // Раскрываем сразу: пользователь выбрал разбиение, чтобы увидеть результат, а не
  // чтобы получить шеврон и второй клик
  const onSplit = (node, attr) => {
    patch(node, (n) => splitNode(n, attr, data.stock, data.rules));
    setOpenPaths((prev) => new Set(prev).add(pathKey(node)));
  };

  const onCollapse = (node) => {
    if (pricedInside(node) && collapseConfirm !== node) {
      setCollapseConfirm(node);
      return;
    }
    setCollapseConfirm(null);
    patch(node, collapseNode);
  };

  const onPrice = (node, raw) => patch(node, (n) => ({ ...n, price: parseMoney(raw) }));

  // Варианты, которые можно добавить веткой вручную: всё, что есть в справочнике,
  // минус то, что уже показано. Для года и объёма справочника нет — там ввод руками
  const missingValues = (node) => {
    const attr = node.by;
    const shown = new Set(node.kids.map((k) => String(k.fixed[attr] ?? '')));
    if (attr === 'wdId')
      return (data?.wd || [])
        .filter((w) => !shown.has(String(w.id)))
        .map((w) => ({ value: w.id, title: w.title }));
    if (attr === 'complectationId')
      return (data?.complectations || [])
        .filter((c) => c.modelId === node.fixed.modelId && !shown.has(String(c.id)))
        .map((c) => ({ value: c.id, title: c.title }));
    return null; // год и объём — свободный ввод
  };

  const submitDraft = (node) => {
    const draft = (addDraft[pathKey(node)] || '').trim();
    if (!draft) return;
    onAddBranch(node, draft);
    setAddDraft((prev) => ({ ...prev, [pathKey(node)]: '' }));
  };

  const onAddBranch = (node, rawValue) => {
    const attr = node.by;
    let value;
    if (attr === 'wdId' || attr === 'complectationId') {
      value = Number(rawValue);
    } else if (attr === 'engineVolume') {
      // Приводим к тому же виду, что и ветки стока: иначе «2500» не совпало бы
      // ни с одной машиной и ветка показывала бы «нет в стоке» до сохранения
      value = normalizeEngineVolume(rawValue);
      if (!value) {
        alert.error(`Не удалось разобрать объём «${rawValue}». Укажите литры (2.0) или см³ (1975)`);
        return;
      }
    } else {
      value = String(rawValue).trim();
    }
    if (!value) return;
    patch(node, (n) => addBranch(n, attr, value, data.stock, data.rules));
    setOpenPaths((prev) => new Set(prev).add(pathKey(node)));
  };

  const saveRules = async () => {
    setBusy(true);
    try {
      // Дерево строится из стока И из правил, поэтому правила с моделью отражает
      // целиком. Исключение — правило без modelId («на всю марку»): верхний уровень
      // дерева всегда модель, показать его негде. Дописываем такие обратно, иначе
      // сохранение молча стирало бы их, ведь набор заменяется целиком
      const payload = [...treeToRules(tree), ...unrepresentableRules(data.rules || [])];
      await savePriceRules(markId, payload);
      alert.success(`Прайс сохранён: правил — ${payload.length}`);
      load(markId);
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось сохранить прайс');
    } finally {
      setBusy(false);
    }
  };

  const saveMeta = async (patchMeta) => {
    const next = { ...data.priceList, ...patchMeta };
    setData({ ...data, priceList: next });
    try {
      await savePriceMeta(markId, {
        docNumber: next.docNumber,
        docDate: next.docDate,
        docUrl: next.docUrl,
      });
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось сохранить шапку документа');
    }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const res = await applyPriceList(markId, true);
      setPreview(res.data);
      setAppliedBatch(null);
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось посчитать предпросмотр');
    } finally {
      setBusy(false);
    }
  };

  const runApply = async () => {
    // Первый клик — предпросмотр: считаем, сколько машин заденем, и ждём второго
    if (!applyConfirm) {
      setBusy(true);
      try {
        const res = await applyPriceList(markId, true);
        setPreview(res.data);
        setAppliedBatch(null);
        setApplyConfirm(res.data);
      } catch (err) {
        alert.error(extractErrorMessage(err) || 'Не удалось посчитать предпросмотр');
      } finally {
        setBusy(false);
      }
      return;
    }
    setApplyConfirm(null);
    setBusy(true);
    try {
      const res = await applyPriceList(markId, false);
      alert.success(`Применено: изменено машин — ${res.data.changed}`);
      setPreview(res.data);
      setAppliedBatch(res.data.changed > 0 ? res.data.batchId : null);
      load(markId);
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось применить прайс');
    } finally {
      setBusy(false);
    }
  };

  const runRollback = async () => {
    if (!appliedBatch) return;
    setBusy(true);
    try {
      const res = await rollbackPriceList(appliedBatch);
      alert.success(`Откат: возвращено машин — ${res.data.reverted}`);
      setPreview(null);
      setAppliedBatch(null);
      load(markId);
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось откатить применение');
    } finally {
      setBusy(false);
    }
  };

  const renderNode = (node, depth, inherited) => {
    const key = pathKey(node);
    const count = node.groups.reduce((s, g) => s + g.count, 0);
    // Ветка может быть пустой: её завели под вариант из приказа, которого в стоке
    // ещё нет. Math.min от пустого массива даёт Infinity — показываем прочерк
    const hasCars = node.groups.length > 0;
    const lo = hasCars ? Math.min(...node.groups.map((g) => g.minPrice)) : null;
    const hi = hasCars ? Math.max(...node.groups.map((g) => g.maxPrice)) : null;
    const free = ATTRS.filter((a) => !(a.key in node.fixed));
    const isNotSet = label(node.fixed, depth) === NOT_SET;
    const lost = pricedInside(node);
    const effective = node.price != null ? node.price : inherited;
    const open = isOpen(node);
    // Разбитая ветка без единой машины детей не имеет — но раскрывать её надо:
    // внутри строка «добавить вариант», ради которой разбиение и делали
    const openable = node.kids.length > 0 || !!node.by;
    // Значки — везде, где содержимое ветки не показано: и на свёрнутой ветке, и на
    // листе. Иначе модель без единого правила выглядела бы как готовая, хотя именно
    // из таких и складывается счётчик «без цены». У раскрытой ветки значки не
    // рисуем — то же самое видно построчно ниже
    // Считаем всегда, а не только ради значка: по этой же цифре краснеет поле
    // цены. На раскрытой ветке значков нет, и без подсветки след дыры терялся
    // ровно в тот момент, когда менеджер разворачивал дерево, идя к ней
    const rowStats = subtreeStats(node, inherited);
    const badge = open ? null : rowStats;

    return (
      <React.Fragment key={key}>
        <div
          className={[
            styles.row,
            isNotSet ? styles.rowNotSet : '',
            depth === 0 ? styles.rowRoot : '',
            openable ? styles.rowOpenable : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={(e) => {
            // Клик по строке раскрывает ветку, но не отбирает клики у поля цены и
            // контролов — иначе нельзя было бы поставить курсор в цену
            if (!openable) return;
            if (e.target.closest('input, select, button')) return;
            toggleOpen(node);
          }}
        >
          <span className={depth === 0 ? styles.nameRoot : styles.name}>
            {Array.from({ length: depth }, (_, i) => (
              <span key={i} className={styles.rail} />
            ))}
            {openable ? (
              <button
                type="button"
                className={styles.chevron}
                aria-expanded={open}
                onClick={() => toggleOpen(node)}
                title={open ? 'Свернуть ветку' : 'Развернуть ветку'}
              >
                {open ? '▾' : '▸'}
              </button>
            ) : (
              <span className={styles.chevronGap} />
            )}
            {label(node.fixed, depth)}
            {badge?.withoutPrice > 0 && (
              <span className={styles.badgeBad}>{badge.withoutPrice} без цены</span>
            )}
            {badge?.changing > 0 && (
              <span className={styles.badge}>{badge.changing} не по прайсу</span>
            )}
          </span>
          <span className={styles.count}>{count}</span>
          <span className={`${styles.range} ${hasCars && lo !== hi ? styles.rangeSpread : ''}`}>
            {!hasCars
              ? 'нет в стоке'
              : lo === hi
              ? money(lo)
              : `${(lo / 1e6).toFixed(1)}–${(hi / 1e6).toFixed(1)} млн`}
          </span>
          <input
            className={[styles.price, rowStats.withoutPrice > 0 ? styles.priceEmpty : '']
              .filter(Boolean)
              .join(' ')}
            aria-label={`Цена: ${label(node.fixed, depth)}`}
            value={money(node.price)}
            placeholder={effective != null ? money(effective) : '—'}
            // Красный цвет сам по себе ничего не объясняет и не виден тем, кто его
            // не различает: на hover поле говорит, сколько машин остались без цены
            title={
              rowStats.withoutPrice > 0
                ? `Машин без цены: ${rowStats.withoutPrice}`
                : node.price == null && effective != null
                ? 'Цена унаследована от ветки выше'
                : undefined
            }
            onChange={(e) => onPrice(node, e.target.value)}
          />
          <span className={styles.control}>
            {node.by ? (
              <button
                type="button"
                className={collapseConfirm === node ? styles.miniDanger : styles.mini}
                onClick={() => onCollapse(node)}
                title="Убрать разбиение: ветка снова станет одной строкой"
              >
                {collapseConfirm === node ? `потерять ${lost} цен?` : 'убрать разбиение'}
              </button>
            ) : (
              free.length > 0 && (
                <select
                  className={styles.split}
                  aria-label="Разбить ветку"
                  value=""
                  onChange={(e) => e.target.value && onSplit(node, e.target.value)}
                >
                  <option value="">разбить по…</option>
                  {/* Разбивать можно всегда, даже когда в стоке один вариант или ни
                      одного: приказ различает то, чего в наличии ещё нет, и запрет
                      не давал завести такую цену заранее. Число рядом — справка,
                      сколько веток даст сам сток */}
                  {free.map((a) => {
                    const variants = new Set(node.groups.map((g) => String(g[a.key] ?? ''))).size;
                    return (
                      <option key={a.key} value={a.key}>
                        {a.title} — в стоке {variants}
                      </option>
                    );
                  })}
                </select>
              )
            )}
          </span>
        </div>
        {open && node.kids.map((k) => renderNode(k, depth + 1, effective))}
        {open && node.by && renderAddBranch(node, depth + 1)}
      </React.Fragment>
    );
  };

  // Приказ нередко называет вариант, которого в наличии ещё нет. Без этой строки
  // цену на него нельзя было завести заранее, и приехавшая машина молча
  // наследовала цену родительской ветки — ровно та ошибка, ради которой всё затевалось
  const renderAddBranch = (node, depth) => {
    const options = missingValues(node);
    const attrTitle = ATTRS.find((a) => a.key === node.by)?.title || '';
    if (options && options.length === 0) return null;

    return (
      <div className={styles.addRow} style={{ paddingLeft: depth * 18 + 22 }}>
        {options ? (
          <select
            className={styles.split}
            aria-label="Добавить вариант"
            value=""
            onChange={(e) => e.target.value && onAddBranch(node, e.target.value)}
          >
            <option value="">+ {attrTitle}, которого нет в стоке…</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.title}
              </option>
            ))}
          </select>
        ) : (
          // Свободный ввод (год, объём) — с явной кнопкой: полагаться на невидимое
          // нажатие Enter нельзя, о нём никто не догадается
          <>
            <input
              className={styles.addInput}
              placeholder={`${attrTitle}, которого нет в стоке`}
              value={addDraft[pathKey(node)] || ''}
              onChange={(e) =>
                setAddDraft((prev) => ({ ...prev, [pathKey(node)]: e.target.value }))
              }
              onKeyDown={(e) => e.key === 'Enter' && submitDraft(node)}
            />
            <button
              type="button"
              className={styles.mini}
              disabled={!(addDraft[pathKey(node)] || '').trim()}
              onClick={() => submitDraft(node)}
            >
              добавить
            </button>
          </>
        )}
      </div>
    );
  };

  // Верхний уровень дерева — модели, и туда renderAddBranch не достаёт: у корней
  // нет родительского узла. Без этой строки прайс марки без стока завести нельзя
  // вовсе, а у марки со стоком — нельзя на модель, которой в наличии ещё нет
  const missingModels = useMemo(() => {
    const shown = new Set(tree.map((n) => String(n.fixed.modelId ?? '')));
    return (data?.models || []).filter((m) => !shown.has(String(m.id)));
  }, [tree, data]);

  const onAddModel = (rawId) => {
    const modelId = Number(rawId);
    setApplyConfirm(null);
    setTree((prev) => [...prev, makeNode({ modelId }, data.stock || [], data.rules || [])]);
  };

  const meta = data?.priceList;

  return (
    <>
      <Header title="Прайс по приказу" icon={icon} />
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <select
            aria-label="Марка"
            value={markId || ''}
            onChange={(e) => setMarkId(Number(e.target.value))}
          >
            {/* Список — весь каталог, а не только марки со стоком: приказ приходит
                раньше машин. Число рядом говорит, где цена уже работает */}
            {marks.map((m) => (
              <option key={m.markId} value={m.markId}>
                {m.mark}
                {m.stock ? ` — ${m.stock} в стоке` : ''}
              </option>
            ))}
          </select>
          {meta && (
            <>
              <input
                placeholder="Номер приказа"
                defaultValue={meta.docNumber || ''}
                onBlur={(e) => saveMeta({ docNumber: e.target.value })}
              />
              <input
                type="date"
                title="Дата документа — она же версия прайса"
                defaultValue={dateInput(meta.docDate)}
                onBlur={(e) => saveMeta({ docDate: e.target.value || null })}
              />
            </>
          )}
        </div>

        {loading ? (
          <div className={styles.empty}>Загрузка…</div>
        ) : !data ? (
          // Без данных пустое дерево значило бы «у марки нет машин», а это неправда
          <div className={styles.empty}>Прайс не загружен</div>
        ) : (
          <>
            <div className={styles.stats}>
              <div className={styles.stat}>
                <span>в стоке</span>
                <b>{stats.total}</b>
              </div>
              <div className={styles.stat}>
                <span>с ценой</span>
                <b className={styles.ok}>{stats.priced}</b>
              </div>
              <div className={styles.stat}>
                <span>без цены</span>
                <b className={stats.withoutPrice ? styles.bad : ''}>{stats.withoutPrice}</b>
              </div>
              <div className={styles.stat}>
                <span>не по прайсу</span>
                <b>{stats.changing}</b>
              </div>
              {/* Заблокированные машины входят в «не по прайсу», но применение их
                  пропустит — показываем отдельно, чтобы разница с предпросмотром
                  была объяснена, а не выглядела расхождением */}
              <div className={styles.stat}>
                <span>из них в сделках</span>
                <b className={stats.locked ? styles.bad : ''}>{stats.locked}</b>
              </div>
            </div>

            <div className={styles.search}>
              <input
                placeholder="Поиск по модели"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" className={styles.mini} onClick={() => setQuery('')}>
                  сбросить
                </button>
              )}
              <button
                type="button"
                className={styles.mini}
                onClick={() => setOpenPaths(new Set())}
              >
                свернуть всё
              </button>
            </div>

            {unrepresentableRules(data?.rules || []).length > 0 && (
              // Правило на всю марку в дереве не отображается — но и не теряется.
              // Молчать о нём нельзя: менеджер не поймёт, откуда у машины цена
              <div className={styles.note}>
                Правил на всю марку (вне дерева):{' '}
                {unrepresentableRules(data.rules).length}. Они сохраняются, но
                редактируются только через базу.
              </div>
            )}

            <div className={styles.head}>
              <span className={styles.name}>Ветка</span>
              <span className={styles.count}>шт</span>
              <span className={styles.range}>сейчас</span>
              <span className={styles.headPrice}>новая цена</span>
              <span className={styles.control} />
            </div>
            {visibleTree.length ? (
              visibleTree.map((n) => renderNode(n, 0, null))
            ) : query.trim() ? (
              <div className={styles.empty}>По запросу «{query}» ничего не найдено</div>
            ) : (
              <div className={styles.empty}>У этой марки нет машин в стоке приложения</div>
            )}
            {!query.trim() && missingModels.length > 0 && (
              <div className={styles.addRow} style={{ paddingLeft: 22 }}>
                <select
                  className={styles.split}
                  aria-label="Добавить модель"
                  value=""
                  onChange={(e) => e.target.value && onAddModel(e.target.value)}
                >
                  <option value="">+ модель, которой нет в стоке…</option>
                  {missingModels.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {preview && (
              <div className={styles.preview}>
                <div>изменится машин — {preview.changed}</div>
                <div>уже совпадает — {preview.unchanged}</div>
                <div>в корзинах у клиентов — {preview.inCart}, сменим и запишем в историю сделки</div>
                <div className={preview.skippedLocked ? styles.bad : ''}>
                  в сделках с заявкой в банк — {preview.skippedLocked}, пропустим
                  {preview.lockedVins?.length ? `: ${preview.lockedVins.join(', ')}` : ''}
                </div>
                <div className={preview.noRule ? styles.bad : ''}>
                  без правила — {preview.noRule}
                </div>
              </div>
            )}

            <div className={styles.actions}>
              <Button text="Сохранить прайс" onClick={saveRules} disabled={busy} fill />
              <Button text="Предпросмотр" onClick={runPreview} disabled={busy} />
              <Button
                text={
                  applyConfirm
                    ? `Изменить ${applyConfirm.changed} машин?`
                    : 'Применить к стоку'
                }
                onClick={runApply}
                disabled={busy || (applyConfirm && applyConfirm.changed === 0)}
                style={applyConfirm ? { borderColor: '#d84a4a', color: '#d84a4a' } : {}}
              />
              {appliedBatch && (
                <Button text="Откатить" onClick={runRollback} disabled={busy} />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default PriceList;
